import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getOverlapSettings,
  updateOverlapSettings,
} from "../services/api_data";
import { toast } from "react-toastify";

export const useOverlapSettings = () => {
  const queryKey = ["overlapSettings"];
  const queryClient = useQueryClient();

  // Fetch overlap settings
  const {
    data: settings,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(queryKey, getOverlapSettings, {
    retry: 1,
    refetchOnWindowFocus: false,
    onError: (error) => {
      console.error("Error fetching overlap settings:", error);
      toast.error("Failed to load overlap settings.");
    },
  });

  // Update overlap settings mutation
  const { mutate: updateSettings, isLoading: isUpdating } = useMutation(
    (settings) => updateOverlapSettings(settings),
    {
      // Optimistic update
      onMutate: async (newSettings) => {
        await queryClient.cancelQueries(queryKey);
        const previousSettings = queryClient.getQueryData(queryKey);

        queryClient.setQueryData(queryKey, {
          overlap_policy: newSettings.overlap_policy,
          require_confirmation: newSettings.require_confirmation,
        });

        return { previousSettings };
      },
      onSuccess: (data) => {
        toast.success(data.message || "Overlap settings updated successfully.");
        queryClient.invalidateQueries(queryKey);
      },
      onError: (error, variables, context) => {
        console.error("Error updating overlap settings:", error);
        toast.error(
          error.response?.data?.detail || "Failed to update overlap settings."
        );
        // Rollback to previous state
        if (context?.previousSettings) {
          queryClient.setQueryData(queryKey, context.previousSettings);
        }
      },
    }
  );

  return {
    settings,
    isLoading,
    isError,
    error,
    updateSettings,
    isUpdating,
    refetch,
  };
};
